import { z } from "zod";
import { Id, IsoDateTime } from "./common.ts";
import { TableStatus } from "./table.ts";

/**
 * Venue floor-plan layout — the spatial model behind the owner editor and the
 * staff / customer viewers. Frontend-first: see
 * docs/superpowers/specs/2026-06-06-floor-plan-design.md.
 *
 * All elements are CENTER-based (`cx`,`cy` + `w`,`h` + `rotation`) so rotate /
 * resize math stays simple. Coordinates are abstract floor units; the renderer
 * maps `floor` onto the SVG `viewBox`.
 */

export const ElementKind = z.enum(["table", "stage", "bar", "dancefloor", "entrance", "zone"]);
export type ElementKind = z.infer<typeof ElementKind>;

export const TableShape = z.enum(["circle", "rect"]);
export type TableShape = z.infer<typeof TableShape>;

/** Geometry + label shared by every element kind. */
const base = {
  id: Id,
  cx: z.number(),
  cy: z.number(),
  w: z.number().positive(),
  h: z.number().positive(),
  rotation: z.number().default(0),
  label: z.string().optional(),
};

export const TableElement = z.object({
  kind: z.literal("table"),
  ...base,
  shape: TableShape.default("circle"),
  seats: z.number().int().min(0).default(4),
  capacity: z.number().int().positive().default(4),
  /** Links to the venue's `Table.code`. Spatial truth lives here for now. */
  code: z.string().min(1),
  status: TableStatus.optional(),
});
export type TableElement = z.infer<typeof TableElement>;

export const StageElement = z.object({ kind: z.literal("stage"), ...base });
export const BarElement = z.object({ kind: z.literal("bar"), ...base });
export const DanceFloorElement = z.object({ kind: z.literal("dancefloor"), ...base });
export const EntranceElement = z.object({ kind: z.literal("entrance"), ...base });

export const ZoneElement = z.object({
  kind: z.literal("zone"),
  ...base,
  /** Optional tint (CSS color / token var). Drawn translucent, behind tables. */
  fill: z.string().optional(),
});
export type ZoneElement = z.infer<typeof ZoneElement>;

export const LayoutElement = z.discriminatedUnion("kind", [
  TableElement,
  StageElement,
  BarElement,
  DanceFloorElement,
  EntranceElement,
  ZoneElement,
]);
export type LayoutElement = z.infer<typeof LayoutElement>;

export const FloorDimensions = z.object({
  width: z.number().positive(),
  height: z.number().positive(),
});
export type FloorDimensions = z.infer<typeof FloorDimensions>;

export const VenueLayout = z.object({
  id: Id,
  branchId: Id,
  name: z.string().min(1),
  floor: FloorDimensions,
  elements: z.array(LayoutElement),
  updatedAt: IsoDateTime,
});
export type VenueLayout = z.infer<typeof VenueLayout>;

/** Kinds that are decorative backdrops, rendered beneath interactive tables. */
export const BACKDROP_KINDS: ReadonlySet<ElementKind> = new Set(["zone", "dancefloor"]);

/** Normalize table codes for layout ↔ operational joins (`T01` → `T1`). */
export function normalizeTableCode(code: string): string {
  const match = code.match(/^([A-Za-z]+)0*(\d+)$/);
  if (!match?.[1] || !match[2]) return code;
  return `${match[1].toUpperCase()}${match[2]}`;
}

export function tableCodesMatch(a: string, b: string): boolean {
  return normalizeTableCode(a) === normalizeTableCode(b);
}

/** Stable paint order: backdrops first, tables last (so they sit on top). */
export function sortByPaintOrder(elements: readonly LayoutElement[]): LayoutElement[] {
  const rank = (k: ElementKind) => (BACKDROP_KINDS.has(k) ? 0 : k === "table" ? 2 : 1);
  return [...elements].sort((a, b) => rank(a.kind) - rank(b.kind));
}
